/**
 * Rybbit sink — self-hosted product analytics at analytics.birdnestlife.com.
 *
 * Replaces Amplitude, whose account is deactivated. The script is loaded once by
 * TrackingProvider; everything here is a no-op until it is on the page, so a
 * blocked or slow script never throws into a click handler.
 *
 * PAGEVIEWS ARE NOT SENT FROM HERE. Rybbit derives them from the URL. The old
 * wrapper hand-wrote a `pageview:${pageId}` event per route, which double-counts
 * every navigation and re-encodes the route table as a list of strings someone
 * has to maintain.
 */

import type { EventName, EventMap, GlobalProperties } from './events';

type RybbitApi = {
  event: (name: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string) => void;
  clearUserId: () => void;
};

declare global {
  interface Window {
    rybbit?: RybbitApi;
  }
}

const api = (): RybbitApi | undefined =>
  typeof window === 'undefined' ? undefined : window.rybbit;

let globals: Partial<GlobalProperties> = {};

/** Set once at init. Merged into every event so call sites never repeat them. */
export const setGlobalProperties = (next: Partial<GlobalProperties>) => {
  globals = { ...globals, ...next };
};

export const rybbitTrack = <E extends EventName>(event: E, properties: EventMap[E]) => {
  try {
    api()?.event(event, { ...globals, ...properties });
  } catch (error) {
    console.error('Rybbit: failed to send', event, error);
  }
};

export const rybbitIdentify = (userId: string) => {
  try {
    api()?.identify(userId);
  } catch (error) {
    console.error('Rybbit: failed to identify', error);
  }
};

export const rybbitReset = () => {
  try {
    api()?.clearUserId();
  } catch (error) {
    console.error('Rybbit: failed to clear user', error);
  }
};
